import React, { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  AppBar,
  IconButton,
  Toolbar,
  Grid,
  Autocomplete,
  TextField,
  Stack,
  Chip,
  Button,
  Typography,
  Select,
  MenuItem,
  Paper,
  Checkbox,
  Alert,
  LinearProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { EventViewModel, SiteViewModel } from '../ViewModel'
import { addNewEvent, getAllSites, getEvent, updateEvent } from '../../model'
import { isNumeric, isStringValid } from '../../utils/isValid'
import { getCurrentDateArray as getCurrentDate } from '../../utils/date'

interface NewEventDialogProps {
  siteId?: string
  eventId?: string
  open: boolean
  onClose: () => void
}

export default function NewEventDialog({
  siteId,
  eventId,
  open,
  onClose,
}: NewEventDialogProps) {
  const [sites, setSites] = useState<SiteViewModel[]>([])

  const [event, setEvent] = useState<EventViewModel>(initialEvent)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState({ value: false, message: '' })

  const isValidEventId = eventId && eventId.trim().length !== 0
  const isValidSiteId = siteId && siteId.trim().length !== 0

  useEffect(() => {
    init()

    return () => {
      setEvent(initialEvent)
      setError({ value: false, message: '' })
    }
  }, [open])

  const init = async () => {
    if (isValidSiteId) {
      setEvent((event) => ({ ...event, siteCode: siteId }))
    }

    if (isValidEventId) {
      const fetchedEvent = await getEvent(siteId, eventId)
      setEvent({ ...initialEvent, ...fetchedEvent })
    }

    const sitesData = await getAllSites()
    setSites(sitesData)
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError({ value: false, message: '' })
    try {
      if (isValidEventId) {
        await updateEvent(event)
      } else {
        await addNewEvent(event)
      }

      onClose()
    } catch (e) {
      setError({ value: true, message: typeof e === 'string' ? e : e.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} fullScreen fullWidth={true} onClose={onClose}>
      <AppBar sx={{ backgroundColor: 'white', position: 'static' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <div />
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Stack p={2} spacing={2}>
        {/* Site and Date */}
        <Grid container>
          <Grid item xs={12} md={6} p={2}>
            <Autocomplete
              disabled={isValidSiteId}
              ListboxProps={{ style: { maxHeight: '10rem' } }}
              freeSolo={true}
              selectOnFocus
              clearOnBlur
              options={sites.map((s) => s.id)}
              getOptionLabel={(option: string) => {
                if (!isStringValid(option)) return ''
                return `${option} - ${
                  sites.find((s) => s.id == option)?.siteName
                }`
              }}
              value={event.siteCode}
              onChange={(_, option: string) => {
                setEvent((event) => ({ ...event, siteCode: option }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={false}
                  label={'Site'}
                  variant="standard"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} p={2}>
            <DateTextField
              dateValue={`${event.creationDate} ${event.timeOfRecord}`}
              onChangeDate={(date) =>
                setEvent((event) => ({ ...event, creationDate: date }))
              }
              onChangeTime={(time) => {
                setEvent((event) => ({ ...event, timeOfRecord: time }))
              }}
            />
          </Grid>
        </Grid>

        {/* Ranger */}
        <Autocomplete
          onChange={(_, newValue) => {
            setEvent((event) => ({ ...event, samplers: newValue }))
          }}
          multiple
          id="tags-filled"
          options={[].map((option) => option.name)}
          freeSolo
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                key={index}
                variant="outlined"
                sx={{ borderRadius: 2 }}
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="filled"
              label="Rangers"
              placeholder="Enter to add ranger"
            />
          )}
        />

        {/* Water Level + Current Weather */}
        <Grid container>
          <Grid item xs={12} md={6} p={2}>
            <EventTF
              label="Water Level"
              number
              defaultText={event.waterLevel}
              onChangeText={(t) => {
                setEvent((event) => ({
                  ...event,
                  waterLevel: Number(t),
                }))
              }}
            />
            <EventSelect
              label="Flow"
              value={event.flowObservation}
              options={[
                { label: 'None', value: '' },
                { label: 'Baseline', value: 'baseline' },
                { label: 'Fresh', value: 'fresh' },
                { label: 'Flood(minor)', value: 'flood(minor)' },
                { label: 'Flood(moderate)', value: 'flood(moderate)' },
                { label: 'Flood(major)', value: 'flood(major)' },
              ]}
              onChangeOption={(option: string) => {
                setEvent((event) => ({
                  ...event,
                  flowObservation: option,
                }))
              }}
            />
            <EventTF
              label="Flow Rate"
              number
              defaultText={event.flowRate}
              onChangeText={(text) => {
                setEvent((event) => ({
                  ...event,
                  flowRate: Number(text),
                }))
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} p={2}>
            <Typography fontWeight={'bold'}>Current Weather</Typography>
            <Paper elevation={1} sx={{ padding: 2 }}>
              <EventSelect
                label="Current Rainfall"
                value={event.rainfallObservation}
                options={[
                  { label: 'None', value: '' },
                  { label: 'Light', value: 'light' },
                  { label: 'Medium', value: 'medium' },
                  { label: 'Heavy', value: 'heavy' },
                ]}
                onChangeOption={(option: string) => {
                  setEvent((event) => ({
                    ...event,
                    rainfallObservation: option,
                  }))
                }}
              />
              <EventSelect
                label="Wind"
                value={event.windObservation}
                options={[
                  { label: 'None', value: '' },
                  { label: 'Light', value: 'light' },
                  { label: 'Moderate', value: 'moderate' },
                  { label: 'Strong', value: 'strong' },
                ]}
                onChangeOption={(option: string) => {
                  setEvent((event) => ({
                    ...event,
                    windObservation: option,
                  }))
                }}
              />
              <EventSelect
                label="Sky"
                value={event.skyObservation}
                options={[
                  { label: 'None', value: '' },
                  { label: 'Sunny', value: 'sunny' },
                  { label: 'Cloudy', value: 'cloudy' },
                  { label: 'Overcast', value: 'overcast' },
                ]}
                onChangeOption={(option: string) => {
                  setEvent((event) => ({
                    ...event,
                    skyObservation: option,
                  }))
                }}
              />
              <EventTF
                label="Air Temperature"
                number
                defaultText={event.airTemp}
                onChangeText={(t) => {
                  setEvent((event) => ({ ...event, airTemp: Number(t) }))
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Water Quality Parameter */}
        <Stack p={2}>
          <Typography fontWeight={'bold'}>Water Quality Parameters</Typography>
          <Paper elevation={2}>
            <Grid container columnGap={10}>
              <Grid item xs={12} md={3}>
                <EventTF
                  label="Depth of sample"
                  number
                  defaultText={event.sampleDepth}
                  onChangeText={(t) => {
                    setEvent((event) => ({ ...event, sampleDepth: Number(t) }))
                  }}
                />
                <EventTF
                  label="Temperature"
                  number
                  defaultText={event.waterTemp}
                  onChangeText={(t) => {
                    setEvent((event) => ({ ...event, waterTemp: Number(t) }))
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <EventTF
                  label="pH"
                  number
                  defaultText={event.pH}
                  onChangeText={(t) => {
                    setEvent((event) => ({ ...event, pH: Number(t) }))
                  }}
                />
                <EventTF
                  label="EC"
                  number
                  defaultText={event.EC}
                  onChangeText={(t) => {
                    setEvent((event) => ({ ...event, EC: Number(t) }))
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <EventTF
                  label="Turbidity"
                  number
                  defaultText={event.turbidity}
                  onChangeText={(t) => {
                    setEvent((event) => ({ ...event, turbidity: Number(t) }))
                  }}
                />
                <TextField
                  label="Note any other parameters if taken (e.g. DO, PO4):"
                  value={event.waterNotes}
                  fullWidth
                  onChange={(e) => {
                    setEvent((event) => ({
                      ...event,
                      waterNotes: e.target.value,
                    }))
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Stack>

        {/* Rainfall + Flood Event */}
        <Grid container>
          <Grid
            item
            xs={12}
            md={6}
            p={2}
            display="flex"
            flexDirection={'column'}
          >
            <Typography fontWeight={'bold'}>
              Rainfall prior to day of sampling
            </Typography>
            <Paper elevation={2} sx={{ height: '100%' }}>
              <EventTF
                label="Past 24"
                number
                defaultText={event.pastRainfall.past24}
                onChangeText={(t) => {
                  setEvent((event) => ({
                    ...event,
                    pastRainfall: { ...event.pastRainfall, past24: Number(t) },
                  }))
                }}
              />
              <EventTF
                label="Past week"
                number
                defaultText={event.pastRainfall.pastWeek}
                onChangeText={(t) => {
                  setEvent((event) => ({
                    ...event,
                    pastRainfall: {
                      ...event.pastRainfall,
                      pastWeek: Number(t),
                    },
                  }))
                }}
              />
              <EventTF
                label="Past month"
                number
                defaultText={event.pastRainfall.pastMonth}
                onChangeText={(t) => {
                  setEvent((event) => ({
                    ...event,
                    pastRainfall: {
                      ...event.pastRainfall,
                      pastMonth: Number(t),
                    },
                  }))
                }}
              />
            </Paper>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            p={2}
            display="flex"
            flexDirection={'column'}
          >
            <Typography fontWeight={'bold'}>
              Flood Event Monitoring Section Only
            </Typography>
            <Paper elevation={2}>
              <EventCheckbox
                label="Lab Sample Taken"
                value={event.labSample}
                onChangeChecked={(checked) => {
                  setEvent((event) => ({
                    ...event,
                    labSample: checked,
                  }))
                }}
              />
              <EventCheckbox
                label="A Bottle (Major ions)"
                value={event.aBottle}
                onChangeChecked={(checked) => {
                  setEvent((event) => ({
                    ...event,
                    aBottle: checked,
                  }))
                }}
              />
              <EventCheckbox
                label="D Bottle (Nutrients - unfiltered)"
                value={event.dBottle}
                onChangeChecked={(checked) => {
                  setEvent((event) => ({
                    ...event,
                    dBottle: checked,
                  }))
                }}
              />
              <EventTF
                label="Flood Comments"
                defaultText={event.floodComments}
                onChangeText={(text: string) => {
                  setEvent((event) => ({ ...event, floodComments: text }))
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Index + Notes */}
        <Grid container>
          <Grid item xs={12} md={6} p={2}>
            <Typography fontWeight={'bold'}>Macroinvertable data</Typography>
            <Paper elevation={3}>
              <EventTF
                label="Signal Index"
                number
                defaultText={event.signalIndex}
                onChangeText={(t) => {
                  setEvent((event) => ({ ...event, signalIndex: Number(t) }))
                }}
              />
              <EventTF
                label="Pollution Index"
                number
                defaultText={event.pollutionIndex}
                onChangeText={(t) => {
                  setEvent((event) => ({ ...event, pollutionIndex: Number(t) }))
                }}
              />
              <EventTF
                label="Catchment Index"
                number
                defaultText={event.catchmentIndex}
                onChangeText={(t) => {
                  setEvent((event) => ({ ...event, catchmentIndex: Number(t) }))
                }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} p={2}>
            <TextField
              label="Notes"
              multiline
              fullWidth
              rows={5}
              value={event.siteNotes}
              variant="filled"
              onChange={(e) => {
                setEvent((event) => ({ ...event, siteNotes: e.target.value }))
              }}
            />
          </Grid>
        </Grid>
        <div>
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
          >
            {isValidEventId ? 'Update' : 'Add'} Event
          </Button>
          {loading && <LinearProgress />}
        </div>
        {error.value && (
          <Alert severity="error">
            {error.message ?? 'Unable to add event! Please try again.'}
          </Alert>
        )}
      </Stack>
    </Dialog>
  )
}

interface SelectOption {
  value: string
  label: string
}
function EventSelect({
  label,
  value,
  options,
  onChangeOption,
}: {
  label: string
  value: string
  options: SelectOption[]
  onChangeOption: (option: string) => void
}) {
  return (
    <Stack direction={'row'} p={1} spacing={2}>
      <Typography width={200}>{label}</Typography>
      <Select
        fullWidth
        variant="standard"
        value={value ?? ''}
        onChange={(e) => {
          onChangeOption(e.target.value)
        }}
      >
        {options.map((option, index) => {
          return (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          )
        })}
      </Select>
    </Stack>
  )
}

function EventTF({
  label,
  defaultText,
  number,
  onChangeText,
}: {
  label: string
  defaultText: string | number
  /**
   * If true, the text field will validate if the text is number
   */
  number?: boolean
  onChangeText: (text: string) => void
}) {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState({ value: false, message: '' })
  const tfRef = useRef<HTMLInputElement>()

  useEffect(() => {
    if (
      defaultText &&
      (defaultText.toString().trim().length !== 0 ||
        defaultText.toString().trim() !== '0')
    ) {
      setInputValue(`${defaultText}`)
    }

    return () => {
      setInputValue('')
    }
  }, [])

  return (
    <Stack direction={'row'} p={1} spacing={2}>
      <Typography width={label.length < 5 ? 50 : 200}>{label}</Typography>
      <TextField
        fullWidth
        ref={tfRef}
        variant="standard"
        value={inputValue}
        onChange={(e) => {
          const value = e.target.value

          // Change input value to 0 when user tries to delete last element
          if (number && !value && value.trim().length === 0) {
            setInputValue('')
            onChangeText(value)
            return
          }

          // Keep '.' for potential double number, but not update text
          if (number && value.charAt(value.length - 1) === '.') {
            setInputValue(value)
            return
          }

          // Validate number
          if (number && !isNumeric(value)) {
            setError({ value: true, message: 'Please enter a number' })
            return
          }
          setError({ value: false, message: '' })
          setInputValue(value)
          onChangeText(value)
        }}
        onBlur={(e) => {
          const value = e.target.value
          if (number && !isNumeric(value)) {
            setError({ value: true, message: 'Please enter a number' })
            return
          }

          setError({ value: false, message: '' })

          // Process for number text field
          if (number) {
            const num = Number(value)
            setInputValue(`${num === 0 ? '' : num}`)
            onChangeText(`${num}`)
            return
          }
          onChangeText(value)
        }}
        error={error.value}
        helperText={error.message}
      />
    </Stack>
  )
}

function EventCheckbox({
  label,
  value,
  onChangeChecked,
}: {
  label: string
  value: boolean
  onChangeChecked: (checked: boolean) => void
}) {
  return (
    <Stack direction={'row'} p={1} spacing={2}>
      <Typography>{label}</Typography>
      <Checkbox
        checked={value ?? false}
        onChange={(e) => {
          onChangeChecked(e.target.checked)
        }}
      />
    </Stack>
  )
}

function DateTextField({
  dateValue,
  onChangeDate,
  onChangeTime,
}: {
  dateValue: string
  onChangeDate: (date: string) => void
  onChangeTime: (time: string) => void
}) {
  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  const hourRef = useRef<HTMLInputElement>(null)
  const minRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState({
    day: false,
    month: false,
    year: false,
    hour: false,
    minute: false,
  })
  const [date, setDate] = useState({
    day: '',
    month: '',
    year: '',
    hour: '',
    minute: '',
  })

  const currentDate = getCurrentDate()

  useEffect(() => {
    if (!dateValue || dateValue.trim().length === 0) {
      setDate(currentDate)
    } else {
      const dateString = dateValue.split(' ')
      const date = dateString[0].split('/')
      const time = dateString[1].split(':')

      setDate({
        day: date[0],
        month: date[1],
        year: date[2],
        hour: time[0],
        minute: time[1],
      })
    }
  }, [])

  useEffect(() => {
    const day = date.day.trim().length !== 0 ? date.day : currentDate.day
    const month =
      date.month.trim().length !== 0 ? date.month : currentDate.month
    const year = date.year.trim().length !== 0 ? date.year : currentDate.year
    const hour = date.hour.trim().length !== 0 ? date.hour : 12
    const minute = date.minute.trim().length !== 0 ? date.minute : 0

    onChangeDate(`${day}/${month}/${year}`)
    onChangeTime(`${hour}:${minute}`)
  }, [date])

  function handleKeyboardEventForDate(
    id: string,
    e: React.KeyboardEvent<HTMLDivElement>
  ) {
    const dayInput = dayRef.current
    const monthInput = monthRef.current
    const yearInput = yearRef.current
    const hourInput = hourRef.current
    const minuteInput = minRef.current

    if (e.key === 'Backspace') {
      id === 'month' && monthInput.value.length === 0 && dayInput.focus()
      id === 'year' && yearInput.value.length === 0 && monthRef.current.focus()
      id === 'hour' && hourInput.value.length === 0 && yearInput.focus()
      id === 'minute' && minuteInput.value.length === 0 && hourInput.focus()
    } else {
      id === 'day' &&
        dayInput.value.length == dayInput.maxLength &&
        monthInput.focus()
      id === 'month' &&
        monthInput.value.length === monthInput.maxLength &&
        yearInput.focus()
      id === 'year' &&
        yearInput.value.length === yearInput.maxLength &&
        hourInput.focus()
      id === 'hour' &&
        hourInput.value.length === hourInput.maxLength &&
        minuteInput.focus()
    }
  }

  const handleDateInputOnChange = (
    id: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value

    // Validate number
    if (!isNumeric(value)) {
      // Enable empty value in text field for backspace
      if (value.length !== 0) return
    }
    setDate((date) => ({ ...date, [id]: value }))
  }

  return (
    <Stack direction={'row'} alignItems="end" spacing={2} mt={2}>
      <TextField
        variant="standard"
        inputRef={dayRef}
        type="text"
        placeholder="DD"
        inputProps={{ maxLength: 2 }}
        // sx={{ width: '100%', boxSizing: 'border-box' }}
        onKeyDown={(e) => handleKeyboardEventForDate('day', e)}
        value={date.day}
        onChange={(e) => handleDateInputOnChange('day', e)}
        onBlur={() => {
          if (date.day.length === 0) setError((e) => ({ ...e, day: true }))
          else setError((e) => ({ ...e, day: false }))
        }}
        error={error.day}
      />
      <div>/</div>
      <TextField
        variant="standard"
        inputRef={monthRef}
        type="text"
        placeholder="MM"
        inputProps={{ maxLength: 2 }}
        // sx={{ width: '100%', boxSizing: 'border-box' }}
        onKeyDown={(e) => handleKeyboardEventForDate('month', e)}
        value={date.month}
        onChange={(e) => handleDateInputOnChange('month', e)}
        onBlur={() => {
          if (date.month.length === 0) setError((e) => ({ ...e, month: true }))
          else setError((e) => ({ ...e, month: false }))
        }}
        error={error.month}
      />
      <div>/</div>
      <TextField
        variant="standard"
        inputRef={yearRef}
        type="text"
        placeholder="YYYY"
        onKeyDown={(e) => handleKeyboardEventForDate('year', e)}
        inputProps={{ maxLength: 4 }}
        // sx={{ width: '100%', boxSizing: 'border-box' }}
        value={date.year}
        onChange={(e) => handleDateInputOnChange('year', e)}
        onBlur={() => {
          if (date.year.length === 0) setError((e) => ({ ...e, year: true }))
          else setError((e) => ({ ...e, year: false }))
        }}
        error={error.year}
      />
      <div></div>
      <TextField
        variant="standard"
        type="text"
        inputRef={hourRef}
        placeholder="hh"
        onKeyDown={(e) => handleKeyboardEventForDate('hour', e)}
        inputProps={{ maxLength: 2 }}
        value={date.hour}
        onChange={(e) => handleDateInputOnChange('hour', e)}
        onBlur={() => {
          if (date.hour.length === 0) setError((e) => ({ ...e, hour: true }))
          else setError((e) => ({ ...e, hour: false }))
        }}
        error={error.hour}
      />
      :
      <TextField
        variant="standard"
        type="text"
        inputRef={minRef}
        placeholder="mm"
        onKeyDown={(e) => handleKeyboardEventForDate('minute', e)}
        inputProps={{ maxLength: 2 }}
        value={date.minute}
        onChange={(e) => handleDateInputOnChange('minute', e)}
        onBlur={() => {
          if (date.minute.length === 0)
            setError((e) => ({ ...e, minute: true }))
          else setError((e) => ({ ...e, minute: false }))
        }}
        error={error.minute}
      />
    </Stack>
  )
}

const initialEvent: EventViewModel = {
  id: '',
  siteCode: '',
  creationDate: '',
  timeOfRecord: '',
  samplers: [],
  waterLevel: 0,
  flowObservation: '',
  waterlevelObservation: '',
  flowRate: 0,
  rainfallObservation: '',
  windObservation: '',
  skyObservation: '',
  airTemp: 0,
  sampleDepth: 0,
  waterTemp: 0,
  pH: 0,
  EC: 0,
  turbidity: 0,
  waterNotes: '',
  pastRainfall: {
    past24: 0,
    pastWeek: 0,
    pastMonth: 0,
  },
  labSample: false,
  aBottle: false,
  dBottle: false,
  floodComments: '',
  signalIndex: 0,
  pollutionIndex: 0,
  catchmentIndex: 0,
  siteNotes: '',
}
